/* MENASA pages: <menasa-page> (sections/menasa-contact, menasa-club, menasa-info, menasa-not-found and
   snippets/menasa-policy-nav). The heritage page's motion language for the house's other pages: words rise out of
   masks as they reach the screen, pictures are uncovered by a moving window once they have loaded, rules draw out.
   Also: the navbar takes the tone of a dark page (Orient Club) and gives it back at the footer; the contact letter's
   subject stamp and its sending; one-line notes sized to their line; the questions open and close by height; the care pages' index follows the reading.
   Works with the site's page transitions (menasa-artistic-transitions.js): no inline scripts, every listener is
   removed when the page leaves, and Back shows the page as it rests. */
(() => {
  'use strict';
  if (customElements.get('menasa-page')) return;

  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const GLASS_VARS = ['--nav-rgb', '--header-top', '--header-bottom'];
  const IVORY = [250, 247, 239], INK = [33, 27, 23];
  // A section's tone under the navbar: [ground, ink, glass opacity at the top].
  const TONES = { ivory: [IVORY, INK, .94], dusk: [[27, 17, 15], IVORY, .78], navy: [[19, 27, 43], IVORY, .9] };
  const clamp = (v, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const mix = (a, b, t) => a.map((v, i) => lerp(v, b[i], t));
  const wait = ms => new Promise(r => setTimeout(r, ms));
  // Phones and the light effects tier: small labels rise word by word (letter by letter is dozens of moving layers).
  const lightTier = () => { const q = window.MenasaQuality; return !!(q && (q.phone || q.tier === 'light' || q.tier === 'reduced')); };

  // Wraps each word in a mask (.mp-w > .mp-wi) or each letter (.mp-c), numbered for the stagger. Text stays text.
  function split(el, chars) {
    if (el.mpParts) return el.mpParts;
    const parts = [], nodes = [];
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    for (let n = walker.nextNode(); n; n = walker.nextNode()) if (n.textContent.trim()) nodes.push(n);
    for (const node of nodes) {
      const frag = document.createDocumentFragment();
      for (const token of node.textContent.split(/(\s+)/)) {
        if (!token) continue;
        if (!token.trim()) { frag.append(' '); continue; }
        if (chars) {
          const word = document.createElement('span');
          word.className = 'mp-cw';
          for (const c of token) {
            const s = document.createElement('span');
            s.className = 'mp-c';
            s.textContent = c;
            s.style.setProperty('--i', parts.length);
            parts.push(s);
            word.append(s);
          }
          frag.append(word);
        } else {
          const mask = document.createElement('span'), w = document.createElement('span');
          mask.className = 'mp-w';
          w.className = 'mp-wi';
          w.textContent = token;
          w.style.setProperty('--i', parts.length);
          parts.push(w);
          mask.append(w);
          frag.append(mask);
        }
      }
      node.replaceWith(frag);
    }
    el.mpParts = parts;
    return parts;
  }

  // The subject stamp's face (as in sections/menasa-contact.liquid), for the envelope on the "sent" page.
  function stampNode(caption, label) {
    const make = (cls, text) => { const el = document.createElement('span'); el.className = cls; if (text) el.textContent = text; return el; };
    const paper = make('mp-stamp__paper'), art = make('mp-stamp__art');
    if (caption) art.append(make('mp-stamp__caption', caption));
    art.append(make('mp-crest'), make('mp-stamp__label', label));
    paper.append(art);
    return paper;
  }

  // A part rises once it reaches the screen; a part holding pictures waits (up to 1.6 s) until they are decoded.
  function show(el) {
    const images = [...el.querySelectorAll('img')].filter(img => !img.complete || !img.naturalWidth);
    images.forEach(img => { img.loading = 'eager'; });
    const ready = images.length ? Promise.race([Promise.all(images.map(img => (img.decode ? img.decode() : Promise.resolve()).catch(() => {}))), wait(1600)]) : Promise.resolve();
    ready.then(() => requestAnimationFrame(() => el.classList.add('is-in')));
  }

  class MenasaPage extends HTMLElement {
    connectedCallback() {
      if (this.alive) return;
      this.alive = true;
      this.off = [];
      this.header = document.getElementById('PrestigeHeader');
      this.letter();
      this.sent();
      this.questions();
      this.signup();
      this.indexes();
      this.fits();
      queueMicrotask(() => { if (this.alive) this.reveal(); });
      if (this.querySelector('[data-mp-tone]')) {
        this.on_(window, 'scroll', () => this.queue(), { passive: true });
        this.on_(window, 'resize', () => this.measureSoon(), { passive: true });
        if (document.fonts && document.fonts.status !== 'loaded') document.fonts.ready.then(() => this.measureSoon());
        // The router clears the header's colours right after inserting the page: paint ours on the next frame.
        requestAnimationFrame(() => this.measureSoon());
      }
    }

    disconnectedCallback() {
      if (!this.alive) return;
      this.alive = false;
      this.off.forEach(f => f());
      this.off = [];
      if (this.io) this.io.disconnect();
      cancelAnimationFrame(this.raf);
      cancelAnimationFrame(this.mraf);
      if (this.toned) {
        if (this.header) this.header.style.removeProperty('--nav-ink');
        if (this.pane) { GLASS_VARS.forEach(n => this.pane.style.removeProperty(n)); this.pane.style.removeProperty('opacity'); }
      }
    }

    on_(target, type, fn, opts) { if (!target) return; target.addEventListener(type, fn, opts); this.off.push(() => target.removeEventListener(type, fn, opts)); }

    /* ── Reveals ─────────────────────────────────────────────────────────────────────────────── */
    reveal() {
      if (reduce.matches || !('IntersectionObserver' in window)) return;
      const chars = !lightTier();
      this.querySelectorAll('[data-split]').forEach(el => split(el, chars && el.dataset.split === 'chars'));
      this.io = new IntersectionObserver(entries => entries.forEach(e => {
        if (!e.isIntersecting) return;
        this.io.unobserve(e.target);
        show(e.target);
      }), { rootMargin: '0px 0px -8% 0px', threshold: .08 });
      this.classList.add('js-reveal');
      this.querySelectorAll('[data-in]').forEach(el => {
        // Already scrolled past (Back to the middle of a page): shown as it rests.
        if (el.getBoundingClientRect().bottom < 0) el.classList.add('is-in');
        else this.io.observe(el);
      });
    }

    /* ── The navbar's tone over a dark page ──────────────────────────────────────────────────── */
    measureSoon() {
      cancelAnimationFrame(this.mraf);
      this.mraf = requestAnimationFrame(() => { if (this.alive) { this.measure(); this.tone(); } });
    }

    measure() {
      this.hh = this.header ? this.header.offsetHeight : 72;
      const marks = [...this.querySelectorAll('[data-mp-tone]')].map(el => ({ tone: TONES[el.dataset.mpTone] || TONES.ivory, top: el.getBoundingClientRect().top + scrollY }));
      // The shared footer below the page is ivory.
      const foot = document.querySelector('[data-menasa-site-footer]');
      if (foot) marks.push({ tone: TONES.ivory, top: foot.getBoundingClientRect().top + scrollY });
      this.tones = marks.sort((a, b) => a.top - b.top);
    }

    queue() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => { this.raf = 0; this.tone(); });
    }

    tone() {
      if (!this.alive || !this.header || !this.tones || !this.tones.length) return;
      this.toned = true;
      const y = scrollY, band = this.hh;
      const first = this.tones[0].tone;
      let rgb = first[0], ink = first[1], top = first[2];
      for (const s of this.tones.slice(1)) {
        const cover = clamp((y + band - s.top) / band);
        if (cover <= 0) continue;
        rgb = mix(rgb, s.tone[0], cover);
        ink = mix(ink, s.tone[1], cover);
        top = lerp(top, s.tone[2], cover);
      }
      const inkS = ink.map(Math.round).join(' '), key = rgb.map(Math.round).join(' ') + '|' + top.toFixed(2);
      let pane = this.pane;
      if (!pane || !pane.isConnected) { pane = this.pane = this.header.querySelector('.site-header__glass'); this.key = undefined; }
      if (inkS !== this.inkS) { this.inkS = inkS; this.header.style.setProperty('--nav-ink', inkS); }
      if (pane && key !== this.key) {
        this.key = key;
        pane.style.setProperty('--nav-rgb', key.split('|')[0]);
        pane.style.setProperty('--header-top', top.toFixed(2));
        pane.style.setProperty('--header-bottom', (top * .82).toFixed(2));
      }
    }

    /* ── Contact: the letter ─────────────────────────────────────────────────────────────────── */
    letter() {
      const form = this.querySelector('[data-mp-letter]');
      if (!form) return;
      const topics = [...this.querySelectorAll('[data-mp-topic]')];
      const subject = form.querySelector('[data-mp-subject]');
      const field = form.querySelector('textarea');
      const pad = this.writing(form, field);
      const write = () => { if (pad) pad.focus(); else if (field) field.focus({ preventScroll: true }); };
      // The subject is a stamp in the letter's corner; it opens a sheet of stamps, and the one chosen is pressed on.
      const stamp = this.querySelector('[data-mp-stamp]');
      const toggle = stamp && stamp.querySelector('[data-mp-stamp-toggle]');
      const sheet = stamp && stamp.querySelector('[data-mp-stamp-sheet]');
      const paper = toggle && toggle.querySelector('.mp-stamp__paper');
      const mark = toggle && toggle.querySelector('.mp-stamp__mark');
      const label = toggle && toggle.querySelector('[data-mp-stamp-label]');
      const fine = matchMedia('(hover: hover) and (pointer: fine)');
      const isOpen = () => !!stamp && stamp.classList.contains('is-open');
      const open = state => {
        if (!sheet || isOpen() === state) return;
        stamp.classList.toggle('is-open', state);
        toggle.setAttribute('aria-expanded', String(state));
      };
      const face = btn => {
        if (label) label.textContent = btn.dataset.mpLabel || btn.textContent.trim();
        if (btn.dataset.mpTone) toggle.style.setProperty('--tone', btn.dataset.mpTone);
      };
      let peeling = null;
      const swap = btn => {
        if (peeling) { peeling.cancel(); peeling = null; }
        if (!paper || !paper.animate || reduce.matches) { face(btn); return; }
        // The old stamp peels away, the new one is pressed on and the postmark struck again.
        const out = peeling = paper.animate([{ transform: 'none', opacity: 1 }, { transform: 'translate(8px,-16px) rotate(10deg)', opacity: 0 }], { duration: 230, easing: 'cubic-bezier(.5,0,.75,0)', fill: 'forwards' });
        out.onfinish = () => {
          if (peeling !== out) return;
          peeling = null;
          face(btn);
          out.cancel();
          paper.animate([{ transform: 'translateY(-12px) rotate(-6deg) scale(1.14)', opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 560, easing: 'cubic-bezier(.22,.7,.16,1)' });
          if (mark) mark.animate([{ transform: 'scale(1.3)', opacity: 0 }, { transform: 'none', opacity: .9 }], { duration: 480, delay: 240, easing: 'cubic-bezier(.22,.7,.16,1)', fill: 'backwards' });
        };
      };
      const choose = (btn, focus, animate) => {
        const changed = btn.getAttribute('aria-pressed') !== 'true';
        topics.forEach(t => t.setAttribute('aria-pressed', String(t === btn)));
        if (subject) subject.value = btn.dataset.mpTopic;
        if (btn.dataset.mpPrompt) { if (field) field.placeholder = btn.dataset.mpPrompt; if (pad) pad.prompt(btn.dataset.mpPrompt); }
        if (toggle) { if (animate && changed) swap(btn); else face(btn); }
        open(false);
        // With a mouse, straight on to writing; on a touch screen, back to the stamp (no keyboard springing up).
        if (!focus) return;
        if (toggle && !fine.matches) toggle.focus({ preventScroll: true });
        else write();
      };
      topics.forEach(t => this.on_(t, 'click', () => choose(t, true, true)));
      if (toggle && sheet) {
        sheet.hidden = false;
        this.on_(toggle, 'click', () => {
          const state = !isOpen();
          open(state);
          if (state) requestAnimationFrame(() => { const at = topics.find(t => t.getAttribute('aria-pressed') === 'true') || topics[0]; if (at && isOpen()) at.focus({ preventScroll: true }); });
        });
        this.on_(document, 'pointerdown', e => { if (isOpen() && !stamp.contains(e.target)) open(false); });
        this.on_(stamp, 'keydown', e => { if (e.key === 'Escape' && isOpen()) { e.preventDefault(); open(false); toggle.focus({ preventScroll: true }); } });
        this.on_(stamp, 'focusout', e => { if (e.relatedTarget && !stamp.contains(e.relatedTarget)) open(false); });
        // Theme Editor: selecting a topic block shows it on the stamp.
        this.on_(this, 'shopify:block:select', e => { const t = e.target.closest && e.target.closest('[data-mp-topic]'); if (t) choose(t, false, false); });
      }
      // A link such as /pages/contact?topic=orient-club arrives with that topic chosen.
      const asked = new URLSearchParams(location.search).get('topic');
      const pre = asked && topics.find(t => t.dataset.mpKey === asked);
      if (pre) choose(pre, false, false);
      // Without the writing area, the textarea grows with what is written (the ruled lines grow with it).
      if (field && !pad) {
        const grow = () => { field.style.height = 'auto'; field.style.height = Math.max(field.scrollHeight, field.offsetHeight) + 'px'; };
        this.on_(field, 'input', grow);
      }
      const btn = form.querySelector('.mp-letter__send [type="submit"]');
      const envelope = this.post(form, pad, toggle);
      // "Send the letter" (once the form is valid) seals the letter into its envelope; only the seal sends it.
      if (envelope) this.on_(form, 'submit', e => {
        if (envelope.through()) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        open(false);
        envelope.start();
      }, true);
      this.sending(form, on => {
        if (btn) {
          btn.classList.toggle('is-busy', on);
          if (on) btn.setAttribute('aria-busy', 'true'); else btn.removeAttribute('aria-busy');
        }
        if (envelope) envelope.busy(on);
      });
    }

    /* ── Contact: the letter into its envelope ──────────────────────────────────────────────────
       Folded in three, slipped into the envelope and the flap closed; the sender's name and email written on it, the
       subject stamp put on and wax poured at the flap's point. The wax is the form's send button ("Send" shows on
       it): pressed, Shopify's spam check runs first; once it passes, a brass stamp brings the house's crest down into
       the wax, the envelope flies off and the letter goes. The envelope's details are kept for the page that says it
       was sent. Reduced motion: the same steps, without movement. */
    post(form, pad, toggle) {
      const stage = form.querySelector('[data-mp-post]');
      const letter = form.querySelector('.mp-letter');
      if (!stage || !letter || !stage.animate) return null;
      const $ = s => stage.querySelector(s);
      const scene = $('.mp-post__scene'), sheet = $('.mp-post__sheet'), flap = $('.mp-env__flapwrap'), front = $('.mp-env__front');
      const top = $('.mp-post__panel--top'), bottom = $('.mp-post__panel--bottom'), words = $('[data-mp-post-words]');
      const nameEl = $('[data-mp-post-name]'), emailEl = $('[data-mp-post-email]'), stampEl = $('[data-mp-post-stamp]');
      const seal = $('[data-mp-seal]'), wax = $('.mp-seal__wax'), well = $('.mp-seal__well'), press = $('.mp-seal__press');
      const foot = $('.mp-post__foot'), hint = $('[data-mp-post-hint]'), edit = $('[data-mp-post-edit]');
      const ease = 'cubic-bezier(.22,.7,.16,1)', crease = 'cubic-bezier(.65,0,.35,1)';
      let state = 'letter', run = 0, through = false, base = 0;
      const live = new Set();
      const play = (el, frames, o) => {
        const a = el.animate(frames, { fill: 'both', easing: ease, ...o, duration: reduce.matches ? 0 : o.duration, delay: reduce.matches ? 0 : o.delay || 0 });
        live.add(a);
        return a;
      };
      const pause = ms => wait(reduce.matches ? 0 : ms);
      const value = name => ((form.elements[name] && form.elements[name].value) || '').trim();
      const toTop = el => {
        const at = el.getBoundingClientRect().top + scrollY - (this.header ? this.header.offsetHeight : 72) - 16;
        if (Math.abs(scrollY - at) > 40) scrollTo({ top: at, behavior: reduce.matches ? 'auto' : 'smooth' });
      };

      // Letter v2 (assets/menasa-letter-v2.js, section setting "Letter style"): the same steps, drawn in 3D on a canvas
      // behind the letter. Without WebGL 2, with reduced motion, or if its script has not come, this envelope (v1).
      const host = form.closest('[data-letter]');
      let v2 = null, v2base = 0;
      if (host && host.dataset.letter === 'v2' && !reduce.matches) {
        const make = () => {
          const api = window.MenasaLetterV2;
          if (v2 || !this.alive || !api || !api.supported()) return;
          let assets = {};
          try { assets = JSON.parse(host.dataset.letterAssets || '{}'); } catch (err) { /* the paper still draws; no wax */ }
          const label = seal.querySelector('.mp-seal__label');
          v2 = api.create(form, { assets, words: { seal: label ? label.textContent.trim() : '' } });
          if (v2) this.off.push(() => v2.destroy());
        };
        if (window.MenasaLetterV2) make();
        else this.on_(document, 'menasa:letter-v2', make);
      }
      const v2on = () => !!v2 && stage.classList.contains('is-v2');
      // Under the drawn envelope: its words and "Unfold to edit"; the stage keeps room for them.
      const fitV2 = () => { stage.style.height = Math.max(letter.offsetHeight, v2base + foot.offsetHeight + 16) + 'px'; };
      const placeV2 = () => {
        const geo = v2.geometry(), sb = stage.getBoundingClientRect(), fb = form.getBoundingClientRect();
        const ox = sb.left - fb.left, oy = sb.top - fb.top;
        Object.assign(seal.style, { left: geo.seal.x - ox - geo.seal.d / 2 + 'px', top: geo.seal.y - oy - geo.seal.d / 2 + 'px', width: geo.seal.d + 'px', height: geo.seal.d + 'px', margin: '0' });
        v2base = geo.envelope.bottom - oy + 20;
        foot.style.top = v2base + 'px';
        fitV2();
        return { geo, ox, oy };
      };
      const startV2 = async () => {
        state = 'folding';
        const id = ++run;
        const gone = () => id !== run || !this.alive;
        stage.hidden = false;
        stage.classList.add('is-v2');
        if (seal.parentElement !== stage) stage.append(seal);
        stage.style.height = letter.offsetHeight + 'px';
        toTop(letter);
        // the canvas takes the letter in the frame it first draws its words; the page's own words step aside
        const ok = await v2.start(() => letter.classList.add('is-posted', 'is-v2-taken'));
        if (gone()) return;
        if (!ok) { state = 'letter'; stage.hidden = true; stage.classList.remove('is-v2'); letter.classList.remove('is-posted', 'is-v2-taken'); return; }
        const { geo } = placeV2();
        play(foot, [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }], { duration: 480 });
        stage.classList.add('is-ready');
        seal.disabled = false;
        state = 'sealed';
        if (!matchMedia('(hover: hover)').matches) v2.hover(.8);
        seal.focus({ preventScroll: true });
        // the envelope and its words in view
        const view = form.getBoundingClientRect().top + geo.envelope.bottom + foot.offsetHeight + 40;
        if (view > innerHeight) scrollTo({ top: scrollY + view - innerHeight, behavior: 'smooth' });
      };

      const start = async () => {
        if (state !== 'letter') return;
        if (v2) return startV2();
        state = 'folding';
        const id = ++run;
        const gone = () => id !== run || !this.alive;
        // What goes on the envelope.
        const name = value('contact[name]'), email = value('contact[email]');
        nameEl.textContent = name || email;
        emailEl.textContent = name ? email : '';
        words.textContent = (pad ? pad.el.innerText : value('contact[body]')).slice(0, 320);
        stampEl.replaceChildren();
        const art = toggle && toggle.querySelector('.mp-stamp__paper');
        if (art) {
          stampEl.append(art.cloneNode(true));
          stampEl.style.setProperty('--tone', toggle.style.getPropertyValue('--tone'));
        }
        // The stage takes the letter's place; the envelope waits below, with room above it for its open flap.
        const was = letter.getBoundingClientRect();
        stage.hidden = false;
        letter.classList.add('is-posted');
        stage.style.height = was.height + 'px';
        const box = stage.getBoundingClientRect();
        const L = { x: was.left - box.left, y: was.top - box.top, w: was.width, h: was.height };
        const room = Math.round(scene.offsetHeight * .56) + 30;
        scene.style.top = room + 'px';
        const E = { x: scene.offsetLeft, y: room, w: scene.offsetWidth, h: scene.offsetHeight };
        Object.assign(sheet.style, { left: L.x - E.x + 'px', top: L.y - E.y + 'px', width: L.w + 'px', height: L.h + 'px' });
        toTop(stage);
        // 1. The letter becomes a sheet of paper and is folded in three.
        play(letter, [{ opacity: 1 }, { opacity: 0 }], { duration: 300 });
        await play(sheet, [{ opacity: 0 }, { opacity: 1 }], { duration: 300 }).finished;
        if (gone()) return;
        play(bottom, [{ transform: 'rotateX(0deg)' }, { transform: 'rotateX(180deg) translateZ(-1px)' }], { duration: 640, easing: crease });
        await pause(400);
        await play(top, [{ transform: 'rotateX(0deg)' }, { transform: 'rotateX(-180deg) translateZ(-2px)' }], { duration: 640, easing: crease }).finished;
        if (gone()) return;
        // 2. The envelope opens; the folded letter comes over it and slips in.
        const P = { x: E.w * .06, y: E.h * .12, w: E.w * .88, h: E.h * .72 };
        const sx = P.w / L.w, sy = P.h / (L.h / 3);
        const tx = P.x - (L.x - E.x), ty = P.y - (L.y - E.y) - (L.h / 3) * sy;
        const over = `translate(${tx}px,${ty - P.y - P.h - 26}px) scale(${sx},${sy})`, inside = `translate(${tx}px,${ty}px) scale(${sx},${sy})`;
        play(front, [{ opacity: 0, transform: 'translateY(26px)' }, { opacity: 1, transform: 'none' }], { duration: 560 });
        play(flap, [{ opacity: 0 }, { opacity: 1 }], { duration: 560 });
        await play(sheet, [{ transform: 'none' }, { transform: over }], { duration: 760, easing: 'cubic-bezier(.45,0,.2,1)' }).finished;
        if (gone()) return;
        await play(sheet, [{ transform: over }, { transform: inside }], { duration: 560, easing: 'cubic-bezier(.5,0,.25,1)' }).finished;
        if (gone()) return;
        // 3. The flap closes; the envelope rises to the top and the page shortens to it.
        flap.style.zIndex = 4;
        await play(flap, [{ transform: 'rotateX(180deg)' }, { transform: 'rotateX(0deg)' }], { duration: 640, easing: crease }).finished;
        if (gone()) return;
        sheet.style.visibility = 'hidden';
        base = 8 + E.h + 30 + 8;
        const end = base + foot.offsetHeight;
        play(scene, [{ transform: 'none' }, { transform: `translateY(${8 - room}px)` }], { duration: 640 });
        await play(stage, [{ height: L.h + 'px' }, { height: end + 'px' }], { duration: 640 }).finished;
        if (gone()) return;
        // 4. Addressed: the name, then the email, written out.
        for (const line of [nameEl, emailEl]) {
          if (!line.textContent) continue;
          await play(line, [{ clipPath: 'inset(0 100% 0 0)' }, { clipPath: 'inset(0 0% 0 0)' }], { duration: Math.min(1100, 260 + line.textContent.length * 38), easing: 'cubic-bezier(.4,.1,.6,1)' }).finished;
          if (gone()) return;
        }
        // 5. The stamp is put on.
        if (stampEl.firstChild) {
          await play(stampEl, [{ opacity: 0, transform: 'translate(-12px,-20px) rotate(-12deg) scale(1.35)' }, { opacity: 1, transform: 'rotate(4deg)' }], { duration: 520 }).finished;
          if (gone()) return;
        }
        // 6. Wax is poured at the flap's point and settles into a seal with a bevelled well.
        play(seal, [{ opacity: 0 }, { opacity: 1 }], { duration: 120 });
        await play(wax, [{ transform: 'scale(0)' }, { transform: 'scale(1.16)', offset: .45 }, { transform: 'scale(.95)', offset: .72 }, { transform: 'scale(1)' }], { duration: 760 }).finished;
        if (gone()) return;
        play(well, [{ opacity: 0, transform: 'scale(.8)' }, { opacity: 1, transform: 'none' }], { duration: 420 });
        play(foot, [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }], { duration: 480 });
        stage.classList.add('is-ready');
        seal.disabled = false;
        state = 'sealed';
        seal.focus({ preventScroll: true });
      };

      // Pressed, the seal first waits on Shopify's spam check (a ring breathes out from it; any puzzle hCaptcha asks
      // for opens over this page). Once the check has passed, the crest comes down into the wax and the letter is sent
      // from this page; when Shopify has it, the envelope flies off and the words appear where it was. The visitor
      // never leaves the page: where Shopify would send them to its /challenge page, its check is asked for here.
      // Shopify's hCaptcha keeps the form's own submit() when it wires itself to the form, and calls it with the token
      // in place: so the form's submit() is, from here on, that confirmation and the send.
      const captcha = () => !!(window.Shopify && window.Shopify.captcha);
      const done = $('[data-mp-post-done]'), verify = $('[data-mp-post-verify]');
      let pulse = null, asking = null;
      const calm = () => { seal.classList.remove('is-checking'); if (pulse) { pulse.cancel(); pulse = null; } if (v2on()) v2.checking(false); };
      // The stage keeps room under the envelope for its foot (the words, a check, "Unfold to edit").
      const fit = () => {
        if (v2on()) { fitV2(); return; }
        if (!base || stage.hidden) return;
        const from = stage.offsetHeight, to = base + foot.offsetHeight;
        if (Math.abs(from - to) < 2) return;
        live.forEach(a => { if (a.effect && a.effect.target === stage) a.cancel(); });
        play(stage, [{ height: from + 'px' }, { height: to + 'px' }], { duration: 420 });
      };
      // Shopify's /challenge page, read rather than visited: its form (#captcha_form) carries hidden fields (a one-time
      // authenticity_token, skp) and its hCaptcha has a site key of its own. Its script posts them to the form's action
      // with the letter the tab kept under form_key (sessionStorage) and return_path; the letter is sent the same way.
      const readChallenge = (html, url) => {
        const page = new DOMParser().parseFromString(html, 'text/html');
        const challenge = page.querySelector('#captcha_form');
        const key = html.match(/sitekey\s*:\s*["']([0-9a-f-]{20,})["']/i);
        if (!challenge || !key) return null;
        const at = new URL(url, location.href);
        return {
          sitekey: key[1],
          fields: [...challenge.querySelectorAll('input[type="hidden"][name]')].map(input => [input.name, input.value]),
          returnPath: at.searchParams.get('return_path') || location.pathname,
          formKey: at.searchParams.get('form_key') || ''
        };
      };
      // After a refused check (400) Shopify does not say where to go, and its redirect can come back unreadable: its
      // challenge page is asked for, for this letter (the form_key of the redirect, or the one on the form).
      const askChallenge = async from => {
        let key = form.elements.form_key && form.elements.form_key.value;
        try { key = new URL(from, location.href).searchParams.get('form_key') || key; } catch (err) { /* the form's own */ }
        if (!key) return null;
        try {
          const res = await fetch(`/challenge?form_key=${encodeURIComponent(key)}&form_type=contact&return_path=${encodeURIComponent(location.pathname)}`, { credentials: 'same-origin' });
          return res.ok ? readChallenge(await res.text(), res.url) : null;
        } catch (err) {
          return null;
        }
      };
      // The send itself: the form as Shopify would receive it, with the check's token (or, after Shopify's further
      // check, as its challenge page would post it). A contact form that arrives is answered by a redirect to the page
      // with ?contact_posted=true; a visitor Shopify still doubts would be sent to its /challenge page (a check it
      // refused is a 400); anything else is the page again, with the form's errors.
      const deliver = async passed => {
        try {
          const action = (form.getAttribute('action') || '/contact').split('#')[0];
          const body = new URLSearchParams(new FormData(form));
          if (passed) {
            body.delete('h-captcha-response');
            body.delete('g-recaptcha-response');
            passed.fields.forEach(([name, value]) => body.set(name, value));
            body.set('return_path', passed.returnPath);
            if (passed.formKey && !body.get('form_key')) body.set('form_key', passed.formKey);
            body.append('h-captcha-response', passed.token);
          }
          const res = await fetch(action, { method: 'POST', body, credentials: 'same-origin', headers: { Accept: 'text/html' } });
          const html = await res.text();
          if (/[?&]contact_posted=true/.test(res.url) || html.includes('data-mp-sealed')) return { ok: true };
          if (/\/challenge/.test(res.url)) return { recheck: readChallenge(html, res.url) || await askChallenge(res.url) };
          if (res.status === 400) return { recheck: await askChallenge() };
          const page = new DOMParser().parseFromString(html, 'text/html');
          const error = page.querySelector('#ContactForm .mp-status--error');
          return { message: error ? error.textContent.replace(/\s+/g, ' ').trim() : '' };
        } catch (err) {
          return { message: '' };
        }
      };
      // hCaptcha's script, if Shopify's check has not loaded it (the same script and options Shopify uses).
      const hcaptchaReady = () => new Promise(resolve => {
        if (window.hcaptcha && window.hcaptcha.render) { resolve(window.hcaptcha); return; }
        const name = `menasaHcaptcha${Date.now()}`;
        const timer = setTimeout(() => resolve(window.hcaptcha || null), 15000);
        window[name] = () => { clearTimeout(timer); delete window[name]; resolve(window.hcaptcha || null); };
        document.head.append(Object.assign(document.createElement('script'), { async: true, src: `https://js.hcaptcha.com/1/api.js?render=explicit&recaptchacompat=off&onload=${name}` }));
      });
      // Shopify's further check, on this page: its challenge's hCaptcha box (its own site key), under the envelope.
      // Ticked (and any puzzle it opens over the page solved), its token comes back.
      const recheck = challenge => new Promise(async resolve => {
        const hc = challenge && verify ? await hcaptchaReady() : null;
        if (!hc || !hc.render || !this.alive || state !== 'leaving') { resolve(null); return; }
        const key = challenge.sitekey;
        let id = null;
        const finish = token => {
          if (asking !== finish) return;
          asking = null;
          stage.classList.remove('is-asking');
          try { if (id !== null) hc.remove(id); } catch (err) { /* already gone */ }
          verify.replaceChildren();
          verify.hidden = true;
          fit();
          resolve(token || null);
        };
        asking = finish;
        stage.classList.add('is-asking');
        if (stage.dataset.recheck) hint.textContent = stage.dataset.recheck;
        const box = document.createElement('div');
        verify.replaceChildren(box);
        verify.hidden = false;
        try { id = hc.render(box, { sitekey: key, theme: 'light', callback: finish, 'error-callback': () => finish(null), 'expired-callback': () => {}, 'chalexpired-callback': () => {} }); } catch (err) { finish(null); return; }
        fit();
      });
      // The crest comes down into the wax.
      const stamp = async () => {
        if (v2on()) { await v2.press(); seal.classList.add('is-pressed'); return; }
        if (reduce.matches) { seal.classList.add('is-pressed'); return; }
        await play(press, [{ opacity: 0, transform: 'translateY(-110px) scale(1.2)' }, { opacity: 1, transform: 'none' }], { duration: 420, easing: 'cubic-bezier(.55,0,.8,.3)' }).finished;
        seal.classList.add('is-pressed');
        play(wax, [{ transform: 'scale(1)' }, { transform: 'scale(1.07,.93)', offset: .4 }, { transform: 'scale(1)' }], { duration: 320 });
        await pause(260);
        await play(press, [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-96px) scale(1.12)' }], { duration: 480 }).finished;
      };
      const leave = async () => {
        if (state === 'leaving') return;
        state = 'leaving';
        stage.classList.add('is-leaving');
        calm();
        let result = null, passed = null;
        for (let round = 0; round < 3; round++) {
          const sent = deliver(passed);
          await stamp();
          result = await sent;
          if (!this.alive || state !== 'leaving') return;
          if (!('recheck' in result)) break;
          // Shopify wants one more check: the seal lifts, the check appears under the envelope, then it goes again.
          seal.classList.remove('is-pressed');
          stage.classList.remove('is-leaving');
          const token = await recheck(result.recheck);
          if (!this.alive || state !== 'leaving') return;
          if (!token) { result = { message: '' }; break; }
          passed = { ...result.recheck, token };
          stage.classList.add('is-leaving');
          if (stage.dataset.checking) hint.textContent = stage.dataset.checking;
        }
        if (!result || !result.ok) {
          // Not sent: the envelope stays, says why, and the seal can be pressed again (or the letter unfolded).
          state = 'sealed';
          stage.classList.remove('is-leaving');
          seal.classList.remove('is-pressed');
          if (v2on()) v2.sealed();
          hint.textContent = (result && result.message) || stage.dataset.error || hint.textContent;
          fit();
          return;
        }
        try { sessionStorage.removeItem('menasa:letter'); } catch (err) { /* nothing kept */ }
        if (stage.dataset.sending) hint.textContent = stage.dataset.sending;
        if (v2on()) {
          // Letter v2: it turns, takes the stamp and postmark, flies; the words come up under the engraving it leaves.
          seal.disabled = true;
          await v2.send();
          if (!this.alive || !done) return;
          const geo = v2.geometry(), oy = stage.getBoundingClientRect().top - form.getBoundingClientRect().top;
          foot.style.visibility = 'hidden';
          done.hidden = false;
          done.style.top = geo.relief.bottom - oy + 4 + 'px';
          stage.style.height = Math.max(stage.offsetHeight, geo.relief.bottom - oy + done.offsetHeight + 24) + 'px';
          await play(done, [{ opacity: 0, transform: 'translateY(16px)' }, { opacity: 1, transform: 'none' }], { duration: 900 }).finished;
          const heading = done.querySelector('h2');
          if (heading) heading.focus({ preventScroll: true });
          return;
        }
        // Away it goes: a lift, then off over the top right corner; the words take its place.
        if (!reduce.matches) {
          await pause(200);
          const rest = getComputedStyle(scene).transform, from = rest === 'none' ? '' : rest + ' ';
          await play(scene, [
            { transform: from + 'translate(0,0)', opacity: 1 },
            { transform: from + 'translate(0,-18px) rotate(-1.5deg) scale(1.02)', opacity: 1, offset: .24 },
            { transform: from + 'translate(46vw,-80vh) rotate(-16deg) scale(.38)', opacity: 0 }
          ], { duration: 1400, easing: 'cubic-bezier(.5,0,.72,.18)' }).finished;
        }
        if (!this.alive || !done) return;
        const from = stage.offsetHeight;
        live.forEach(a => { if (a.effect && a.effect.target === stage) a.cancel(); });
        stage.style.height = from + 'px';
        scene.style.visibility = 'hidden';
        foot.style.visibility = 'hidden';
        done.hidden = false;
        const to = done.offsetHeight;
        play(stage, [{ height: from + 'px' }, { height: to + 'px' }], { duration: 640 });
        await play(done, [{ opacity: 0, transform: 'translateY(20px)' }, { opacity: 1, transform: 'none' }], { duration: 760, delay: 120 }).finished;
        const heading = done.querySelector('h2');
        if (heading) heading.focus({ preventScroll: true });
      };
      form.submit = leave;

      this.on_(seal, 'click', e => {
        e.preventDefault();
        if (state !== 'sealed') return;
        state = 'checking';
        seal.classList.add('is-checking');
        if (stage.dataset.checking) hint.textContent = stage.dataset.checking;
        if (v2on()) { v2.hover(0); v2.checking(true); }
        else if (!reduce.matches) pulse = wax.animate([{ transform: 'scale(1)' }, { transform: 'scale(.95)' }, { transform: 'scale(1)' }], { duration: 1500, iterations: Infinity, easing: 'ease-in-out' });
        try {
          sessionStorage.setItem('menasa:letter', JSON.stringify({
            at: Date.now(), name: nameEl.textContent, email: emailEl.textContent,
            caption: (toggle && toggle.querySelector('.mp-stamp__caption') || {}).textContent || '',
            label: (toggle && toggle.querySelector('[data-mp-stamp-label]') || {}).textContent || '',
            tone: toggle ? toggle.style.getPropertyValue('--tone') : ''
          }));
        } catch (err) { /* private windows: the sent page shows the envelope without the address */ }
        through = true;
        // With the spam check, the submit goes through Shopify (which calls leave() once it has passed); without it,
        // the seal is confirmed and the letter sent straight away.
        if (captcha() && form.requestSubmit) form.requestSubmit(seal);
        else leave();
      });

      // Letter v2: "Send" sinks into the soft wax under the pointer (and stays faintly shown on touch screens).
      const hoverV2 = on => { if (v2on() && state === 'sealed') v2.hover(on ? 1 : (matchMedia('(hover: hover)').matches ? 0 : .8)); };
      this.on_(seal, 'pointerenter', () => hoverV2(true));
      this.on_(seal, 'pointerleave', () => hoverV2(false));
      this.on_(seal, 'focus', () => hoverV2(true));
      this.on_(seal, 'blur', () => hoverV2(false));

      // Back to the letter, as it was (letter v2 plays its journey backwards first).
      this.on_(edit, 'click', async () => {
        if (v2on()) {
          if (state === 'unfolding') return;
          const id = ++run;
          state = 'unfolding';
          through = false;
          calm();
          if (asking) asking(null);
          seal.disabled = true;
          play(foot, [{ opacity: 1 }, { opacity: 0 }], { duration: 300 });
          await v2.rewind();
          if (id !== run || !this.alive) return;
          live.forEach(a => a.cancel());
          live.clear();
          state = 'letter';
          stage.classList.remove('is-ready', 'is-leaving', 'is-v2');
          stage.hidden = true;
          stage.style.height = '';
          seal.classList.remove('is-pressed');
          foot.style.visibility = '';
          if (stage.dataset.hint) hint.textContent = stage.dataset.hint;
          letter.classList.remove('is-posted', 'is-v2-taken');
          if (pad) pad.focus();
          return;
        }
        run++;
        live.forEach(a => a.cancel());
        live.clear();
        state = 'letter';
        through = false;
        calm();
        if (asking) asking(null);
        stage.classList.remove('is-ready', 'is-leaving');
        stage.hidden = true;
        stage.style.height = '';
        seal.disabled = true;
        seal.classList.remove('is-pressed');
        flap.style.zIndex = '';
        sheet.style.visibility = '';
        if (stage.dataset.hint) hint.textContent = stage.dataset.hint;
        letter.classList.remove('is-posted');
        letter.animate([{ opacity: 0, transform: 'translateY(14px)' }, { opacity: 1, transform: 'none' }], { duration: reduce.matches ? 0 : 520, easing: ease });
        toTop(letter);
        if (pad) pad.focus();
      });

      return {
        start,
        through: () => through,
        // The check never came back (a challenge closed, a network failure): the seal can be pressed again. A check
        // that passes later still confirms and sends (leave() does not depend on this state).
        busy: on => {
          if (on || state !== 'checking') return;
          state = 'sealed';
          calm();
          if (stage.dataset.hint) hint.textContent = stage.dataset.hint;
        }
      };
    }

    /* ── Contact: sent ───────────────────────────────────────────────────────────────────────────
       The page that says the letter has gone shows its envelope as it left: addressed and stamped (kept by post()
       for this tab, for an hour). */
    sent() {
      const done = this.querySelector('[data-mp-sealed]');
      if (!done) return;
      let data = null;
      try {
        data = JSON.parse(sessionStorage.getItem('menasa:letter') || 'null');
        sessionStorage.removeItem('menasa:letter');
      } catch (err) { data = null; }
      if (!data || Date.now() - (data.at || 0) > 36e5) return;
      const put = (selector, text) => { const el = done.querySelector(selector); if (el && text) el.textContent = text; };
      put('[data-mp-post-name]', data.name);
      put('[data-mp-post-email]', data.email);
      const slot = done.querySelector('[data-mp-post-stamp]');
      if (slot && data.label) {
        slot.append(stampNode(data.caption, data.label));
        if (data.tone) slot.style.setProperty('--tone', data.tone);
      }
    }

    /* ── Sending a form through Shopify's spam check ───────────────────────────────────────────
       Shopify's hCaptcha (content_for_header) wires itself to a contact or customer form on the first focus inside
       it, or on page load with data-shopify-captcha="true". A send made before it is ready is held and nothing more
       happens; a token taken only at the last second gives it little to judge by (more visitors are sent to its
       /challenge page). So: armed on arrival, and a held send goes again as soon as the check is ready. The button
       never stays disabled: it comes back if the page is still here (a challenge closed, a network failure). */
    sending(form, busy) {
      this.arm(form);
      let timer = 0, watch = null;
      const settle = () => { clearTimeout(timer); if (watch) { watch.disconnect(); watch = null; } busy(false); };
      this.on_(form, 'submit', () => {
        busy(true);
        clearTimeout(timer);
        timer = setTimeout(settle, 20000);
        if (form.dataset.hcaptchaBound || watch || !('MutationObserver' in window)) return;
        watch = new MutationObserver(() => {
          if (!form.dataset.hcaptchaBound) return;
          watch.disconnect();
          watch = null;
          if (form.requestSubmit) form.requestSubmit(); else form.submit();
        });
        watch.observe(form, { attributes: true, attributeFilter: ['data-hcaptcha-bound'] });
      });
      this.on_(window, 'pageshow', settle);
      this.off.push(() => { clearTimeout(timer); if (watch) watch.disconnect(); });
    }

    // data-shopify-captcha arms the check at DOMContentLoaded; after the site's own page transitions there is none, so
    // the arming is asked for the way Shopify listens for it: a focus inside the form (once; Shopify marks the form).
    arm(form) {
      if (form.dataset.cptcha || form.dataset.hcaptchaBound) return;
      const field = form.querySelector('input:not([type="hidden"]),textarea');
      if (field) field.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
    }

    // The letter's writing area: a block whose lines wrap beside the stamp (a textarea cannot), opening under
    // "Dear MENASA,". The form's textarea stays beneath it, unseen, and carries the message and its "required" check;
    // when that check fails, the letter says so in its own words and the writing area takes the focus.
    writing(form, field) {
      const holder = field && field.closest('[data-mp-body]');
      if (!holder) return null;
      // Back to a page kept by the router: its writing area is in the kept HTML; keep what was written, build afresh.
      const kept = holder.querySelector('.mp-letter__text');
      const before = kept ? kept.innerText.replace(/\n$/, '') : '';
      holder.querySelectorAll('.mp-letter__text,.mp-letter__hint').forEach(n => n.remove());
      const pad = document.createElement('div');
      pad.className = 'mp-letter__text';
      pad.setAttribute('role', 'textbox');
      pad.setAttribute('aria-multiline', 'true');
      pad.setAttribute('aria-required', 'true');
      const dear = form.querySelector('.mp-letter__dear');
      if (dear && dear.id) pad.setAttribute('aria-labelledby', dear.id);
      let plain = true;
      try { pad.contentEditable = 'plaintext-only'; } catch (e) { plain = false; }
      if (pad.contentEditable !== 'plaintext-only') { plain = false; pad.contentEditable = 'true'; }
      const hint = document.createElement('span');
      hint.className = 'mp-letter__hint';
      hint.setAttribute('aria-hidden', 'true');
      const note = form.querySelector('[data-mp-empty]');
      const read = () => pad.innerText.replace(/ /g, ' ').replace(/\n$/, '');
      const sync = () => {
        const text = read();
        field.value = text;
        hint.hidden = text.length > 0;
        if (text.trim() && note && !note.hidden) { note.hidden = true; pad.removeAttribute('aria-invalid'); }
      };
      const prompt = text => { hint.textContent = text; pad.setAttribute('aria-placeholder', text); };
      const focus = () => {
        pad.focus({ preventScroll: true });
        const range = document.createRange();
        range.selectNodeContents(pad);
        range.collapse(false);
        const selection = getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      };
      prompt(field.placeholder);
      const start = field.value || before;
      if (start) pad.textContent = start;
      field.classList.add('mp-letter__mirror');
      field.tabIndex = -1;
      field.setAttribute('aria-hidden', 'true');
      holder.prepend(pad, hint);
      sync();
      this.on_(pad, 'input', sync);
      // Back from Shopify's /challenge page (?form_key=…): Shopify refills the form at DOMContentLoaded.
      if (document.readyState === 'loading') this.on_(document, 'DOMContentLoaded', () => { if (!read() && field.value) { pad.textContent = field.value; sync(); } });
      if (!plain) {
        // Browsers without plain-text editing: pasted or dropped text arrives as text, without its formatting.
        this.on_(pad, 'paste', e => { e.preventDefault(); document.execCommand('insertText', false, (e.clipboardData || window.clipboardData).getData('text/plain')); });
        this.on_(pad, 'drop', e => e.preventDefault());
      }
      // "Dear MENASA," (the textarea's label) and the browser's own check both arrive at the textarea: write instead.
      this.on_(field, 'focus', focus);
      this.on_(field, 'invalid', e => {
        e.preventDefault();
        if (note) { note.hidden = false; pad.setAttribute('aria-invalid', 'true'); pad.setAttribute('aria-describedby', note.id); }
        focus();
      });
      return { el: pad, prompt, focus };
    }

    /* ── One line, always: the text takes the line's width, never above its size in data-mp-fit ── */
    fits() {
      const els = [...this.querySelectorAll('[data-mp-fit]')];
      if (!els.length) return;
      const range = document.createRange();
      const fit = () => els.forEach(el => {
        const max = parseFloat(el.dataset.mpFit) || 13;
        el.style.fontSize = max + 'px';
        range.selectNodeContents(el);
        const need = range.getBoundingClientRect().width, room = el.clientWidth;
        if (need && room) el.style.fontSize = Math.min(max, Math.floor(max * room / need * 9.95) / 10) + 'px';
      });
      fit();
      if (document.fonts && document.fonts.status !== 'loaded') document.fonts.ready.then(() => { if (this.alive) fit(); });
      if ('ResizeObserver' in window) {
        const widths = new WeakMap();
        const ro = new ResizeObserver(entries => {
          if (entries.some(e => { const w = Math.round(e.contentRect.width); const was = widths.get(e.target); widths.set(e.target, w); return w !== was; })) fit();
        });
        els.forEach(el => ro.observe(el));
        this.off.push(() => ro.disconnect());
      }
    }

    /* ── Questions: open and close by height, one motion ─────────────────────────────────────── */
    questions() {
      this.querySelectorAll('details[data-mp-q]').forEach(d => {
        const sum = d.querySelector('summary'), panel = d.querySelector('.mp-q__a, .mp-rte-wrap');
        if (!sum || !panel) return;
        this.on_(sum, 'click', e => {
          if (reduce.matches) return;
          e.preventDefault();
          if (d.mpAnim) d.mpAnim.cancel();
          const opening = !d.open;
          if (opening) d.open = true;
          const h = panel.scrollHeight;
          d.classList.toggle('is-open', opening);
          const a = d.mpAnim = panel.animate(opening
            ? [{ height: '0px', opacity: 0 }, { height: h + 'px', opacity: 1 }]
            : [{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }],
          { duration: Math.min(700, 320 + h * .5), easing: 'cubic-bezier(.22,.7,.16,1)' });
          a.onfinish = () => { d.mpAnim = null; if (!opening) d.open = false; };
        });
      });
    }

    /* ── Orient Club: the email line ─────────────────────────────────────────────────────────── */
    signup() {
      const form = this.querySelector('[data-mp-signup]');
      if (!form) return;
      this.sending(form, on => form.classList.toggle('is-sending', on));
    }

    /* ── Care pages: the index follows the reading ───────────────────────────────────────────── */
    indexes() {
      const links = [...this.querySelectorAll('.mp-index a[href^="#"]')];
      if (!links.length || !('IntersectionObserver' in window)) return;
      const map = new Map(links.map(a => [a.getAttribute('href').slice(1), a]));
      const seen = new Set();
      const io = new IntersectionObserver(entries => {
        entries.forEach(e => (e.isIntersecting ? seen.add(e.target.id) : seen.delete(e.target.id)));
        const first = [...map.keys()].find(id => seen.has(id));
        if (first) links.forEach(a => a.classList.toggle('is-here', a === map.get(first)));
      }, { rootMargin: '-30% 0px -55% 0px' });
      map.forEach((_, id) => { const el = document.getElementById(id); if (el) io.observe(el); });
      this.off.push(() => io.disconnect());
      links.forEach(a => this.on_(a, 'click', e => {
        const el = document.getElementById(a.getAttribute('href').slice(1));
        if (!el) return;
        e.preventDefault();
        scrollTo({ top: el.getBoundingClientRect().top + scrollY - (this.header ? this.header.offsetHeight : 72) - 24, behavior: reduce.matches ? 'auto' : 'smooth' });
      }));
    }
  }

  customElements.define('menasa-page', MenasaPage);
})();
